import React from 'react'
import { SectionCard } from '../../components/sections/SectionCard'
import { Eye, ArrowUpRight, ArrowDownRight, MousePointerClick, Percent, Globe } from 'lucide-react'
import type { PricingTable } from '@/features/subscriptions/types/pricing-table/pricing-table.types'

interface AnalyticsSectionProps {
  isDetailView?: boolean
  pricingTableData?: PricingTable
  className?: string
}

interface MetricCardProps {
  label: string
  value: string
  change: string
  trend: 'up' | 'down'
  icon: React.ReactNode
}

function MetricCard({ label, value, change, trend, icon }: MetricCardProps) {
  return (
    <div className="flex flex-col gap-2 p-4 rounded-xl border bg-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div className="flex items-center justify-center size-8 rounded-lg bg-muted">
          {icon}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-semibold">{value}</span>
        <span className={`flex items-center gap-0.5 text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
          {trend === 'up' ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
          {change}
        </span>
      </div>
    </div>
  )
}

function getMetrics(table?: PricingTable): MetricCardProps[] {
  return [
    {
      label: 'Views (30d)',
      value: (table?.viewsLast30d ?? 12480).toLocaleString(),
      change: '+18.3%',
      trend: 'up',
      icon: <Eye className="size-4 text-muted-foreground" />,
    },
    {
      label: 'Conversions',
      value: (table?.conversions ?? 312).toLocaleString(),
      change: '+12.1%',
      trend: 'up',
      icon: <MousePointerClick className="size-4 text-muted-foreground" />,
    },
    {
      label: 'Conversion Rate',
      value: `${table?.conversionRate ?? 2.5}%`,
      change: table?.conversionRate && table.conversionRate > 3 ? '+0.4%' : '-0.2%',
      trend: table?.conversionRate && table.conversionRate > 3 ? 'up' : 'down',
      icon: <Percent className="size-4 text-muted-foreground" />,
    },
    {
      label: 'Active Embeds',
      value: (table?.embeds.length ?? 2).toLocaleString(),
      change: '+1',
      trend: 'up',
      icon: <Globe className="size-4 text-muted-foreground" />,
    },
  ]
}

interface WeeklyData {
  week: string
  views: number
  conversions: number
  rate: string
}

function getWeeklyData(table?: PricingTable): WeeklyData[] {
  const baseViews = table?.viewsLast30d ?? 12480
  const weeklyViews = Math.round(baseViews / 4)

  return [
    { week: 'Week 1', views: Math.round(weeklyViews * 0.85), conversions: Math.round(weeklyViews * 0.85 * 0.025), rate: '2.3%' },
    { week: 'Week 2', views: Math.round(weeklyViews * 0.95), conversions: Math.round(weeklyViews * 0.95 * 0.025), rate: '2.4%' },
    { week: 'Week 3', views: Math.round(weeklyViews * 1.05), conversions: Math.round(weeklyViews * 1.05 * 0.026), rate: '2.6%' },
    { week: 'Week 4', views: Math.round(weeklyViews * 1.15), conversions: Math.round(weeklyViews * 1.15 * 0.027), rate: '2.7%' },
  ]
}

export const AnalyticsSection = React.memo<AnalyticsSectionProps>(({ pricingTableData, className }) => {
  const metrics = getMetrics(pricingTableData)
  const weeklyData = getWeeklyData(pricingTableData)

  return (
    <div className="flex flex-col gap-6">
      {/* KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} {...metric} />
        ))}
      </div>

      {/* Weekly Trend Table */}
      <SectionCard title="Weekly Performance" layout="down" className={className}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Period</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Views</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Conversions</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Rate</th>
              </tr>
            </thead>
            <tbody>
              {weeklyData.map((row) => (
                <tr key={row.week} className="border-b last:border-0">
                  <td className="py-3 px-4 font-medium">{row.week}</td>
                  <td className="py-3 px-4 text-right">{row.views.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-medium text-green-600">{row.conversions.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right">{row.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  )
})

AnalyticsSection.displayName = 'AnalyticsSection'
